import React, { useState } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useParentAssistance } from '../contexts/ParentAssistanceContext';
import StudentProfileModal from '../components/StudentProfileModal';
import {
  TrendingUp,
  FileText,
  GraduationCap,
  Calendar,
  Award,
  Clock,
  CheckCircle,
  ArrowRight,
  Sparkles,
  MessageCircle,
  Brain,
  Target,
  User,
  Edit
} from 'lucide-react';

const OverviewSection = () => {
  const {
    selectedStudent,
    studentProgress,
    studentJourneyPhase,
    sharedItems,
    loading
  } = useParentPortal();

  const { smartSuggestions, quickActions } = useParentAssistance();
  const [showProfileModal, setShowProfileModal] = useState(false);

  if (!selectedStudent) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-azure-100 to-azure-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-azure-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          Welcome to Your Parent Portal
        </h2>
        <p className="text-slate-600 max-w-md mx-auto">
          Select a student above to view their college journey progress and provide strategic support.
        </p>
      </div>
    );
  }

  if (loading || !studentProgress) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  const { completedMilestones, totalMilestones, colleges, essays, hasAssessment } = studentProgress;

  // Get recent shared items
  const recentSharedItems = sharedItems.slice(0, 3);

  // Get urgent suggestions
  const urgentSuggestions = smartSuggestions.filter(s => s.priority === 'urgent' || s.priority === 'high');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          {selectedStudent.name || selectedStudent.displayName}'s College Journey
        </h1>
        <p className="text-lg text-slate-600">
          Your strategic support is helping them build a strong path to college success.
        </p>
      </div>

      {/* Key Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Progress Overview - Using strategic slate-900 border for emphasis */}
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6 shadow-sm transition-all duration-150 hover:shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center border border-teal-600">
              <TrendingUp className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Overall Progress</h3>
              <p className="text-sm text-slate-600">Journey milestones</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-slate-900">
                {Math.round((completedMilestones / totalMilestones) * 100)}%
              </span>
              <span className="text-sm text-slate-600">
                {completedMilestones}/{totalMilestones} complete
              </span>
            </div>
            <div className="w-full h-3 bg-stone-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${(completedMilestones / totalMilestones) * 100}%`,
                  background: 'linear-gradient(90deg, #26a69a 0%, #4290f5 50%, #643eef 100%)'
                }}
              />
            </div>
          </div>
        </div>

        {/* College List Status */}
        <div className="bg-white rounded-xl border border-slate-300 p-6 hover:border-slate-500 transition-colors duration-150">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-azure-50 rounded-lg flex items-center justify-center border border-azure-600">
              <GraduationCap className="w-5 h-5 text-azure-700" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">College Strategy</h3>
              <p className="text-sm text-slate-600">Schools researched</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-2xl font-bold text-slate-900">{colleges.length}</div>
            <div className="text-sm text-slate-600">
              {colleges.length < 8 ? 'Building list' : colleges.length < 15 ? 'Strong list' : 'Comprehensive list'}
            </div>
          </div>
        </div>

        {/* Essay Progress */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Essay Development</h3>
              <p className="text-sm text-slate-600">Drafts in progress</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-2xl font-bold text-slate-900">{essays.length}</div>
            <div className="text-sm text-slate-600">
              {essays.length === 0 ? 'Not started' : essays.length < 3 ? 'Getting started' : 'Strong progress'}
            </div>
          </div>
        </div>

        {/* Assessment Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Strategic Assessment</h3>
              <p className="text-sm text-slate-600">Foundation work</p>
            </div>
          </div>
          <div className="space-y-2">
            {hasAssessment ? (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Complete</span>
                </div>
                <div className="text-sm text-slate-600">Strong foundation set</div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gold-600" />
                  <span className="text-sm font-medium text-gold-700">Pending</span>
                </div>
                <div className="text-sm text-slate-600">Ready to begin</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Smart Suggestions Panel */}
      {urgentSuggestions.length > 0 && (
        <div className="bg-gradient-to-r from-azure-50 to-indigo-50 rounded-xl border-2 border-azure-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-azure-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Strategic Opportunities</h3>
              <p className="text-slate-600">Ways to support {selectedStudent.name}'s success right now</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {urgentSuggestions.slice(0, 2).map((suggestion) => (
              <div
                key={suggestion.id}
                className="bg-white rounded-lg border border-azure-200 p-4 cursor-pointer hover:border-azure-400 transition-colors"
                onClick={() => window.location.href = suggestion.route}
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-medium text-slate-900">{suggestion.title}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                    suggestion.priority === 'urgent' ? 'bg-coral-100 text-coral-700' : 'bg-magenta-100 text-magenta-700'
                  }`}>
                    {suggestion.priority}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{suggestion.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-azure-600">{suggestion.action}</span>
                  <ArrowRight className="w-4 h-4 text-azure-600" />
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  💡 {suggestion.reasoning}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Shared Items */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-900">Recent Shared Items</h3>
            <button className="text-sm text-azure-600 hover:text-azure-700 font-medium">
              View all →
            </button>
          </div>

          {recentSharedItems.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">No items shared yet</p>
              <p className="text-sm text-slate-400 mt-1">
                When {selectedStudent.name} shares work, it will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentSharedItems.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">{item.item_title || 'Shared Document'}</h4>
                    <p className="text-sm text-slate-600">
                      {item.shared_at && new Date(item.shared_at).toLocaleDateString()}
                    </p>
                  </div>
                  {item.allow_comments && (
                    <MessageCircle className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-6">Quick Actions</h3>
          <div className="space-y-3">
            {/* Student Profile Action - Primary action with teal */}
            <button
              onClick={() => setShowProfileModal(true)}
              className="w-full flex items-center gap-3 p-3 text-left bg-teal-50 border-2 border-teal-600 rounded-lg hover:bg-teal-100 transition-all duration-150 active:scale-[0.98]"
            >
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-teal-600">
                <User className="w-4 h-4 text-teal-700" />
              </div>
              <div>
                <h4 className="font-medium text-teal-900">View Academic Profile</h4>
                <p className="text-sm text-teal-700">Update grades, test scores, and academic details</p>
              </div>
              <ArrowRight className="w-4 h-4 text-teal-600" />
            </button>

            {quickActions.slice(0, 3).map((action) => (
              <button
                key={action.id}
                onClick={action.onClick}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-azure-100 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-azure-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">{action.label}</h4>
                  <p className="text-sm text-slate-600">{action.description}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Confidence Building Message */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-emerald-900 mb-2">
              You're Making a Real Difference
            </h3>
            <p className="text-emerald-800 mb-3">
              Your strategic support and encouragement are key factors in {selectedStudent.name}'s
              college journey success. Students with engaged parents are significantly more likely
              to feel confident about their college choices and application outcomes.
            </p>
            <div className="text-sm text-emerald-700">
              <strong>Next focus areas:</strong> Continue providing feedback on shared work,
              help with strategic college research, and maintain the supportive environment
              you've established.
            </div>
          </div>
        </div>
      </div>

      {/* Student Profile Modal */}
      <StudentProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </div>
  );
};

export default OverviewSection;